import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { CostStats as CostStatsType } from '../../types';

interface CostStatsProps {
  stats: CostStatsType;
  title?: string;
}

function formatCost(amount: number): string {
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
}

export function CostStats({ stats, title = 'Cost Analysis' }: CostStatsProps) {
  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <div className="w-7 h-7 rounded bg-amber-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-2 mb-2 flex-shrink-0">
        <div className="p-2 bg-surface-50 rounded border border-surface-100">
          <p className="text-[10px] font-medium text-surface-500 uppercase tracking-wide">Average</p>
          <p className="text-sm font-bold text-surface-800">{formatCost(stats.average)}</p>
        </div>
        <div className="p-2 bg-surface-50 rounded border border-surface-100">
          <p className="text-[10px] font-medium text-surface-500 uppercase tracking-wide">Median</p>
          <p className="text-sm font-bold text-surface-800">{formatCost(stats.median)}</p>
        </div>
        <div className="p-2 bg-surface-50 rounded border border-surface-100">
          <p className="text-[10px] font-medium text-surface-500 uppercase tracking-wide">Maximum</p>
          <p className="text-sm font-bold text-danger-dark">{formatCost(stats.max)}</p>
        </div>
        <div className="p-2 bg-amber-50 rounded border border-amber-200">
          <p className="text-[10px] font-medium text-amber-600 uppercase tracking-wide">Total</p>
          <p className="text-sm font-bold text-amber-700">{formatCost(stats.total)}</p>
        </div>
      </div>

      {/* Histogram */}
      {stats.histogram.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.histogram} margin={{ top: 5, right: 5, left: -10, bottom: 25 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="range"
                tick={{ fontSize: 9, fill: '#64748b' }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={40}
              />
              <YAxis tick={{ fontSize: 9, fill: '#64748b' }} allowDecimals={false} width={30} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value: number) => [`${value} scans`, 'Count']}
              />
              <Bar dataKey="count" fill="#BCD9A2" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center text-surface-400">
          <div className="text-center">
            <p>No cost data available</p>
            <p className="text-sm mt-1">Configure pricing in Settings to see cost analysis</p>
          </div>
        </div>
      )}
    </div>
  );
}
